import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiSearch, FiUser, FiX } from 'react-icons/fi';
import { usePermissions } from '../../hooks/usePermissions';
import { postUnifiedSearch } from '../../services/unified-search/unified-search';

export interface ProviderSearchResult {
  id: string;
  label: string;
  specialty?: string;
  practice?: string;
}

interface ProviderSearchSelectProps {
  onProvidersChange: (providers: ProviderSearchResult[]) => void;
  selectedProviders: ProviderSearchResult[];
  placeholder?: string;
  disabled?: boolean;
  duplicateProviders?: Record<string, string>;
}

const MIN_SEARCH_CHARS = 2;
const PAGE_SIZE = 10;

export const ProviderSearchSelect: React.FC<ProviderSearchSelectProps> = ({
  onProvidersChange,
  selectedProviders,
  placeholder = 'Search for providers',
  disabled = false,
  duplicateProviders = {},
}) => {
  const { permissions } = usePermissions();
  const [searchInput, setSearchInput] = useState('');
  const [results, setResults] = useState<ProviderSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  // Cursor token for the next provider page; null means no further pages
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const lastQueryRef = useRef<string>('');
  // Track loaded IDs to detect duplicate/end-of-results pages
  const loadedIdsRef = useRef<Set<string>>(new Set());
  // Monotonic id of the most recent request; responses from older requests
  // are ignored so a slow first search cannot overwrite a newer one
  const requestIdRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const mapProvider = useCallback((provider: unknown): ProviderSearchResult => {
    const p = provider as {
      id: string;
      firstName?: string;
      firstname?: string;
      lastName?: string;
      lastname?: string;
      specialty?: string;
      relatedPractices?: { name?: string }[] | null;
    };
    return {
      id: p.id,
      label:
        `${p.firstName || p.firstname || ''} ${
          p.lastName || p.lastname || ''
        }`.trim() || 'Unknown Provider',
      specialty: p.specialty || '',
      practice: p.relatedPractices?.[0]?.name || '',
    };
  }, []);

  const handleSearch = useCallback(async () => {
    const query = searchInput.trim();
    if (query.length < MIN_SEARCH_CHARS || disabled) return;

    setIsSearching(true);
    setHasError(false);
    setHasSearched(true);
    setIsOpen(false);
    setResults([]);
    setNextPageToken(null);
    // A pending "Load more" is superseded by this search and will skip its own
    // cleanup, so clear its loading flag here to avoid a stuck spinner
    setIsLoadingMore(false);
    lastQueryRef.current = query;
    const requestId = ++requestIdRef.current;

    try {
      const response = await postUnifiedSearch(
        query,
        PAGE_SIZE,
        undefined,
        undefined,
        permissions.isAdmin
      );

      const providers = response.data.providers
        .filter(
          (p: unknown) =>
            p &&
            typeof p === 'object' &&
            'id' in (p as Record<string, unknown>) &&
            (p as { id?: string }).id
        )
        .map(mapProvider);

      if (requestId !== requestIdRef.current) return; // superseded
      const token = response.data.providerNextToken;
      loadedIdsRef.current = new Set(providers.map((p) => p.id));
      setResults(providers);
      setNextPageToken(token && token.trim() !== '' ? token : null);
      setIsOpen(true);
    } catch {
      if (requestId !== requestIdRef.current) return; // superseded
      setHasError(true);
      setResults([]);
      setIsOpen(true);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsSearching(false);
      }
    }
  }, [searchInput, permissions.isAdmin, disabled, mapProvider]);

  const handleLoadMore = useCallback(async () => {
    if (!nextPageToken || !lastQueryRef.current) return;

    setIsLoadingMore(true);
    const requestId = ++requestIdRef.current;
    try {
      const response = await postUnifiedSearch(
        lastQueryRef.current,
        PAGE_SIZE,
        nextPageToken,
        undefined,
        permissions.isAdmin
      );

      const more = response.data.providers
        .filter(
          (p: unknown) =>
            p &&
            typeof p === 'object' &&
            'id' in (p as Record<string, unknown>) &&
            (p as { id?: string }).id
        )
        .map(mapProvider);

      if (requestId !== requestIdRef.current) return; // superseded
      const token = response.data.providerNextToken;
      const newItems = more.filter((p) => !loadedIdsRef.current.has(p.id));
      if (newItems.length === 0) {
        // No unique results returned — we've reached the real end
        setNextPageToken(null);
      } else {
        newItems.forEach((p) => loadedIdsRef.current.add(p.id));
        setResults((prev) => [...prev, ...newItems]);
        setNextPageToken(token && token.trim() !== '' ? token : null);
      }
    } catch {
      // Non-fatal — keep existing results
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [nextPageToken, permissions.isAdmin, mapProvider]);

  const handleSelect = useCallback(
    (provider: ProviderSearchResult) => {
      const alreadySelected = selectedProviders.some(
        (p) => p.id === provider.id
      );
      if (!alreadySelected) {
        onProvidersChange([...selectedProviders, provider]);
      }
      // Keep dropdown open for multi-select
    },
    [selectedProviders, onProvidersChange]
  );

  const handleRemove = useCallback(
    (providerId: string) => {
      onProvidersChange(selectedProviders.filter((p) => p.id !== providerId));
    },
    [selectedProviders, onProvidersChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const isSearchDisabled =
    disabled || searchInput.trim().length < MIN_SEARCH_CHARS;
  const showLoadMore = Boolean(nextPageToken);

  return (
    <Box ref={containerRef} w="100%">
      {/* Search row */}
      <HStack spacing={3} align="stretch">
        <Box position="relative" flex={1}>
          <Box
            position="absolute"
            left="0.875rem"
            top="50%"
            transform="translateY(-50%)"
            color="gray.400"
            pointerEvents="none"
            zIndex={1}
          >
            <FiSearch size={16} />
          </Box>
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            pl="2.625rem"
            h="2.875rem"
            fontSize="sm"
            isDisabled={disabled}
            _placeholder={{ color: 'gray.400', fontSize: 'sm' }}
            _focus={{
              borderColor: 'blue.400',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
            }}
            _hover={{ borderColor: 'gray.400' }}
          />
        </Box>

        <Button
          onClick={handleSearch}
          isLoading={isSearching}
          loadingText="Searching…"
          isDisabled={isSearchDisabled}
          colorScheme="blue"
          h="2.875rem"
          px={6}
          flexShrink={0}
          fontSize="sm"
          fontWeight={600}
        >
          Search
        </Button>
      </HStack>

      {/* Too-short hint */}
      {searchInput.length > 0 && searchInput.trim().length < MIN_SEARCH_CHARS && (
        <Text fontSize="xs" color="gray.500" mt={1.5} ml={1}>
          Enter at least {MIN_SEARCH_CHARS} characters to search
        </Text>
      )}

      {/* Results dropdown */}
      {isOpen && (
        <Box
          mt={1.5}
          bg="white"
          border="1px solid"
          borderColor="blue.100"
          borderRadius="lg"
          boxShadow="0 12px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)"
          overflow="hidden"
        >
          {hasError ? (
            <Box px={4} py={3} bg="red.50">
              <Text fontSize="sm" color="red.600" fontWeight={500}>
                Something went wrong. Please try again.
              </Text>
            </Box>
          ) : results.length === 0 ? (
            <Box px={4} py={4} textAlign="center">
              <Box
                color="gray.300"
                mb={2}
                display="flex"
                justifyContent="center"
              >
                <FiUser size={22} />
              </Box>
              <Text fontSize="sm" color="gray.500" fontWeight={500}>
                No providers found
              </Text>
              <Text fontSize="xs" color="gray.400" mt={0.5}>
                Try a different name or specialty
              </Text>
            </Box>
          ) : (
            <>
              {/* Count header */}
              <Box
                px={4}
                py={2}
                borderBottom="1px solid"
                borderColor="gray.100"
                bg="gray.50"
              >
                <Text
                  fontSize="xs"
                  color="gray.500"
                  fontWeight={600}
                  letterSpacing="0.03em"
                >
                  {results.length} PROVIDER{results.length !== 1 ? 'S' : ''}
                  {showLoadMore ? ' · MORE AVAILABLE' : ' FOUND'}
                </Text>
              </Box>

              {/* Result rows */}
              <Box maxH="15rem" overflowY="auto" role="listbox">
                {results.map((provider) => {
                  const isSelected = selectedProviders.some(
                    (p) => p.id === provider.id
                  );
                  const isDuplicate = provider.id in duplicateProviders;

                  return (
                    <Box
                      as="button"
                      type="button"
                      key={provider.id}
                      w="100%"
                      textAlign="left"
                      px={4}
                      py={3}
                      cursor={isSelected ? 'default' : 'pointer'}
                      bg={isSelected ? 'green.50' : 'white'}
                      _hover={isSelected ? {} : { bg: 'blue.50' }}
                      _focus={{ outline: 'none', boxShadow: isSelected ? 'none' : 'inset 0 0 0 2px var(--chakra-colors-blue-400)' }}
                      borderBottom="1px solid"
                      borderColor="gray.50"
                      onClick={() => !isSelected && handleSelect(provider)}
                      disabled={isSelected}
                      role="option"
                      aria-selected={isSelected}
                      transition="background-color 0.12s ease"
                      _last={{ borderBottom: 'none' }}
                    >
                      <HStack spacing={3} align="flex-start">
                        {/* Avatar icon */}
                        <Box
                          color={isSelected ? 'green.500' : 'blue.400'}
                          flexShrink={0}
                          w="1.875rem"
                          h="1.875rem"
                          bg={isSelected ? 'green.100' : 'blue.50'}
                          borderRadius="full"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          mt={0.5}
                        >
                          {isSelected ? (
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <FiUser size={13} />
                          )}
                        </Box>

                        {/* Provider details */}
                        <Box flex={1} minW={0}>
                          <HStack spacing={1.5} align="center" flexWrap="wrap">
                            <Text
                              fontSize="sm"
                              fontWeight={600}
                              color={isSelected ? 'green.800' : 'gray.800'}
                              lineHeight="short"
                            >
                              {provider.label}
                            </Text>
                            {isDuplicate && (
                              <Badge
                                colorScheme="orange"
                                fontSize="0.6rem"
                                px={1.5}
                                py={0.5}
                                borderRadius="sm"
                              >
                                Duplicate
                              </Badge>
                            )}
                            {isSelected && (
                              <Badge
                                colorScheme="green"
                                fontSize="0.6rem"
                                px={1.5}
                                py={0.5}
                                borderRadius="sm"
                              >
                                Selected
                              </Badge>
                            )}
                          </HStack>
                          {(provider.specialty || provider.practice) && (
                            <Text
                              fontSize="xs"
                              color="gray.500"
                              mt={0.5}
                              lineHeight="short"
                              noOfLines={1}
                            >
                              {[provider.specialty, provider.practice]
                                .filter(Boolean)
                                .join(' · ')}
                            </Text>
                          )}
                        </Box>
                      </HStack>
                    </Box>
                  );
                })}
              </Box>

              {/* Load more footer */}
              {showLoadMore && (
                <Box
                  px={4}
                  py={2.5}
                  borderTop="1px solid"
                  borderColor="gray.100"
                  bg="gray.50"
                >
                  {isLoadingMore ? (
                    <HStack justify="center" spacing={2} py={0.5}>
                      <Spinner size="xs" color="blue.400" />
                      <Text fontSize="xs" color="gray.500">
                        Loading more…
                      </Text>
                    </HStack>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      w="100%"
                      color="blue.500"
                      fontWeight={600}
                      fontSize="xs"
                      leftIcon={<FiChevronDown size={13} />}
                      _hover={{ bg: 'blue.50', color: 'blue.600' }}
                      onClick={handleLoadMore}
                    >
                      Load more results
                    </Button>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Selected providers chips */}
      {selectedProviders.length > 0 && (
        <Box mt={3}>
          <Text
            fontSize="xs"
            fontWeight={600}
            color="gray.500"
            letterSpacing="0.03em"
            mb={1.5}
          >
            {selectedProviders.length} SELECTED
          </Text>
          <Wrap spacing={2}>
            {selectedProviders.map((provider) => (
              <WrapItem key={provider.id}>
                <Tag
                  size="md"
                  bg="blue.50"
                  color="blue.700"
                  border="1px solid"
                  borderColor="blue.200"
                  borderRadius="full"
                  px={3}
                  py={1.5}
                  _hover={{ bg: 'blue.100' }}
                  transition="background-color 0.1s ease"
                >
                  <Box mr={1.5} display="inline-flex" alignItems="center">
                    <FiUser size={11} />
                  </Box>
                  <TagLabel fontSize="xs" fontWeight={600}>
                    {provider.label}
                  </TagLabel>
                  <TagCloseButton
                    onClick={() => handleRemove(provider.id)}
                    color="blue.500"
                    _hover={{ color: 'blue.800', bg: 'transparent' }}
                    aria-label={`Remove ${provider.label}`}
                  >
                    <FiX size={11} />
                  </TagCloseButton>
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      )}

      {/* No results message after a search */}
      {hasSearched && !isOpen && !hasError && !isSearching && results.length === 0 && (
        <Text fontSize="xs" color="gray.500" mt={1.5} ml={1}>
          No providers found. Try a different search term.
        </Text>
      )}
    </Box>
  );
};
